import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import BigNumber from 'bignumber.js';
import { getContract, getProvider, fetchBalance, erc20ABI, watchContractEvent } from '@wagmi/core';

import { bigIntToFloatString } from 'utils/number/bigNumber';
import { zeroContract } from 'config/config-env';

import { setInstrumentPairBalance } from 'store/entities/wallet/slice';
import { selectSelectedInstrument, selectTokens } from 'store/entities/market/selectors';
import { instrumentPairBalanceSelector } from 'store/entities/wallet/selectors';

import { ERC20Events } from 'types';

type CurrencyT = {
  name: string;
  address: `0x${string}`;
  decimals: number;
};

export const useInstrumentPairBalance = (walletAddress: `0x${string}` | undefined) => {
  const dispatch = useDispatch();

  const balance = useSelector(instrumentPairBalanceSelector);
  const selectedInstrument = useSelector(selectSelectedInstrument);
  const tokensList = useSelector(selectTokens);

  const defaultBalance = {
    baseCurrency: {
      amount: new BigNumber(0),
      decimals: 0,
    },
    quoteCurrency: {
      amount: new BigNumber(0),
      decimals: 0,
    },
  };

  const getTokenBalance = async (tokenAddress: string, provider: any) => {
    if (!walletAddress || !tokenAddress) {
      return new BigNumber(0);
    }

    if (tokenAddress === zeroContract) {
      const balance = await fetchBalance({
        address: walletAddress,
      });

      return new BigNumber(balance.value._hex);
    } else {
      const contract = getContract({
        address: tokenAddress,
        abi: erc20ABI,
        signerOrProvider: provider,
      });
      const balance = await contract.balanceOf(walletAddress);

      return new BigNumber(balance._hex);
    }
  };

  const parseSelectedPair = async (baseCurrency: CurrencyT, quoteCurrency: CurrencyT) => {
    if (!walletAddress) {
      return { ...defaultBalance };
    }

    try {
      const provider = getProvider();
      const baseCurrencyBalance: BigNumber = await getTokenBalance(baseCurrency.address, provider);
      const quoteCurrencyBalance: BigNumber = await getTokenBalance(quoteCurrency.address, provider);

      return {
        baseCurrency: {
          amount: baseCurrencyBalance,
          decimals: baseCurrency.decimals,
        },
        quoteCurrency: {
          amount: quoteCurrencyBalance,
          decimals: quoteCurrency.decimals,
        },
      };
    } catch (error) {
      console.log(error);
      return { ...defaultBalance };
    }
  };

  const fetchPairBalance = async (baseCurrency: CurrencyT, quoteCurrency: CurrencyT, isCancelled: boolean) => {
    const tokensBalance = await parseSelectedPair(baseCurrency, quoteCurrency);

    if (!isCancelled) {
      const baseCurrency = bigIntToFloatString(tokensBalance.baseCurrency.amount, tokensBalance.baseCurrency.decimals);
      const quoteCurrency = bigIntToFloatString(
        tokensBalance.quoteCurrency.amount,
        tokensBalance.quoteCurrency.decimals
      );
      dispatch(
        setInstrumentPairBalance({
          baseCurrency: baseCurrency.toString(),
          quoteCurrency: quoteCurrency.toString(),
        })
      );
    }
  };

  const getTokenEventWatcher = (tokenAddress: `0x${string}`, callBack: Function) => {
    if (tokenAddress !== zeroContract) {
      const unwatchTokenEvent = watchContractEvent(
        {
          address: tokenAddress,
          abi: erc20ABI,
          eventName: ERC20Events.Transfer,
        },
        () => {
          callBack();
        }
      );

      return unwatchTokenEvent;
    }

    return () => {};
  };

  useEffect(() => {
    let isCancelled = false;
    let unwatchBaseCurrency: Function;
    let unwatchQuoteCurrency: Function;

    if (selectedInstrument && walletAddress && tokensList) {
      const { base_currency_id, quote_currency_id } = selectedInstrument;

      const baseCurrency: CurrencyT = tokensList[base_currency_id];
      const quoteCurrency: CurrencyT = tokensList[quote_currency_id];
      fetchPairBalance(baseCurrency, quoteCurrency, isCancelled);

      if (baseCurrency) {
        unwatchBaseCurrency = getTokenEventWatcher(baseCurrency.address, () =>
          fetchPairBalance(baseCurrency, quoteCurrency, isCancelled)
        );
      }

      if (quoteCurrency) {
        unwatchQuoteCurrency = getTokenEventWatcher(quoteCurrency.address, () =>
          fetchPairBalance(baseCurrency, quoteCurrency, isCancelled)
        );
      }
    }

    return () => {
      isCancelled = true;
      if (unwatchBaseCurrency) unwatchBaseCurrency();
      if (unwatchQuoteCurrency) unwatchQuoteCurrency();
    };
  }, [selectedInstrument, walletAddress, tokensList]);

  return balance;
};
